import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { EmbeddedCategoryLightSchema } from './models.categories';
import { DateTimeTzSchema, VariousMemoryIdRefsSchema } from './models.generic';
import { CreateIdentitySchema } from './models.identities';
import { LaneConstructorSchema, MemoryConfigSchema } from './models.memory_config';
import { GroupMemoriesBySchema, ToolConfigSchema } from './models.tool_configs';
import { EmbeddedToolLightSchema } from './models.tools';

extendZodWithOpenApi(z);

/**
 * Control date range of memories, e.g GET /1.0/memories?startDate=...&endDate=...
 * @note Included in both HTTP and "massage memories" schemas.
 */
export const MemoryQueriesDateRangeSchema = z.object({
  startDate: DateTimeTzSchema.describe('Memories started after this datetime'),
  endDate: DateTimeTzSchema.describe('Memories started before this datetime'),
});
/**
 * Control retrieval of memories, e.g GET /1.0/memories
 */
export type GetMemoriesQueryParams = z.infer<typeof GetMemoriesQueryParamsSchema>;
export const GetMemoriesQueryParamsSchema = MemoryQueriesDateRangeSchema.extend({
  limit: z.coerce.number().int().optional().default(2000).openapi({
    description: 'Max number of Memories to return',
    example: 2000,
  }),
  page: z.coerce.number().int().optional().default(1).openapi({
    description: 'Page of Memories to return',
    example: 1,
  }),
});

/**
 * The complete configuration object needed to construct MemoryGroups, Lanes, Lists, etc.
 * Composition of `MemoryConfigSchema`, `ToolConfigSchema` (map) and from/to range.
 *
 * @note This is the replacement for old MemoryQueries/ticQuery mess.
 */
export type MemoryConstructionParams = z.infer<typeof MemoryConstructionParamsSchema>;
export const MemoryConstructionParamsSchema = MemoryConfigSchema.merge(
  MemoryQueriesDateRangeSchema
).extend({
  /** User-defined configurations of tools / memories within that tool */
  toolConfigsMap: z
    .record(z.string(), ToolConfigSchema)
    .optional()
    .default({})
    .describe('User-defined configurations of tools / memories within that tool'),
});

// TODO: This proper... see Memory Recorder RFC
export const VerbsEnum = z.enum([
  /** Eg, created a new task */
  'created',
  'updated',
  'deleted',
  'moved',
  'completed',
  'copied',
  /** Eg, commented on a task */
  'commented',
  /** Eg, logged time on a task */
  'logged',
]);
export type MemoryVerbs = z.infer<typeof VerbsEnum>;

export type MemoryTool = z.infer<typeof MemoryToolSchema>;
export const MemoryToolSchema = z
  .object({
    id: z.string().optional().openapi({ description: 'Tool ID, if linked', example: 'github' }),
    title: z
      .string()
      .optional()
      .openapi({ description: 'Tool name, if linked', example: 'Github' }),
    icon_url: z
      .string()
      .optional()
      .nullable()
      .openapi({ description: 'Tool icon URL', example: 'https://...' }),
    raw: z
      .string()
      .nullable()
      .openapi({
        example: 'Github Desktop',
        description: 'Raw app/website/program/etc value, if tracked via Memory Recorder',
      })
      .optional(),
  })
  .strict()
  .nullable();

export const MemoryContextSchema = z.string().nullable().optional().openapi({
  example: 'Timely/Frontend',
  description:
    'Usually references project, workspace, team, etc -- stuff that is not in db, but we may want to group/organize by',
});

/**
 * Reference to a matching TIC resource, computed at query time.
 * Enables linking memories to projects/resources via URI pattern matching.
 *
 * Dual URI strategy:
 * - task_uri: Full URI for task-level matching (e.g., jira/project/ABC/issue/123)
 * - project_uri: Prefix URI for project-level matching (e.g., jira/project/ABC)
 * - uri: Primary URI for backward compatibility (task_uri if present, otherwise project_uri)
 *
 * @see constructURI in memory-parser/parser.ts for URI construction logic
 */
export const MemoryRefSchema = z.object({
  uri: z.string().optional().openapi({
    description:
      'Primary URI (task_uri if present, otherwise project_uri) - for backward compatibility',
    example: 'jira/project/ABC/issue/123',
  }),
  task_uri: z.string().optional().openapi({
    description: 'Task-level URI for matching against task resources',
    example: 'jira/project/ABC/issue/123',
  }),
  project_uri: z.string().optional().openapi({
    description: 'Project-level URI for matching against project/forecast resources',
    example: 'jira/project/ABC',
  }),
});
export type MemoryRef = z.infer<typeof MemoryRefSchema>;

export type Memory = z.infer<typeof MemorySchema>;

export const MemorySourceEnumSchema = z
  .enum(['mac', 'windows', 'linux', 'browser_extension'])
  .openapi({
    example: 'mac',
    description: 'Which Memory App uploaded this Memory?',
  });

export type MemorySourceEnum = z.infer<typeof MemorySourceEnumSchema>;

export const MemoryAgentSchema = z.object({
  external_id: z.string().trim().min(1).optional(),
  provider: z.string().trim().min(1),
  client: z.string().trim().min(1),
  event: z.enum(['prompt_interaction', 'agent_work']),
  session_id: z.string().optional(),
  session_name: z.string().optional(),
});

export const MemorySchema = z.object({
  id: z.number().int().openapi({ description: 'Memory ID', example: 1 }),
  /** Coloring of this Memory in UI, inherited from tool */
  color: z
    .string()
    .nullable()
    .openapi({ description: 'CSS-compatible color of this tool', example: 'rgb(111, 66, 193)' })
    .default('rgb(111, 66, 193)'),
  /** Processed title of this Memory */
  title: z
    .string()
    .openapi({ description: 'Memory title', example: 'Some Pull Request Title Here' }),
  verb: VerbsEnum.optional()
    .nullable()
    .openapi({ description: 'Verb of this Memory', example: 'created' }),
  /** Short summarizing description of this memory */
  description: z
    .string()
    .nullable()
    .openapi({ description: 'Memory description', example: '...' })
    .optional(),
  icon_url: z
    .string()
    .url()
    .optional()
    .nullable()
    .openapi({ description: 'Best icon to represent this Memory', example: 'https://...' }),
  started_at: z
    .string()
    .datetime({ offset: true })
    .openapi({ description: 'Start of this Memory', example: '2021-10-10T10:10:10.000Z' }),
  /** End of this Memory (minimum offset from start time may be imposed) */
  ended_at: z
    .string()
    .datetime({ offset: true })
    .openapi({ description: '(Optional) end of this Memory', example: '2021-10-10T10:23:33.000Z' }),
  updated_at: z.string().datetime({ offset: true }).openapi({
    description: 'Internal record of when this Memory was last touched',
    example: '2021-10-10T10:23:33.000Z',
  }),
  total_seconds: z
    .number()
    .int()
    .openapi({ description: 'Total seconds of this Memory', example: 123 }),
  url: z
    .string()
    // Memory Tracker (and possibly TIC integrations) don't have strong `url`
    // validation, so we'll just make it a string for now.
    // .url()
    .optional()
    .nullable()
    .openapi({ description: 'URL to this Memory', example: 'https://...' }),
  tool: MemoryToolSchema.nullable().optional(),
  window_tool: EmbeddedToolLightSchema.nullable(),
  context: MemoryContextSchema.optional(),
  ignored: z.boolean().optional().default(false).openapi({
    description: 'If true, this Memory has been ignored (via a rule, or explicitly marked)',
    example: true, // msw won't mock `false` as default :(
  }),
  /** Category inherited from Tool */
  category: EmbeddedCategoryLightSchema.nullable().optional(),
  tracker_source: MemorySourceEnumSchema.nullable(),
  agent: MemoryAgentSchema.nullable().optional(),
  from_timely: z.boolean().optional().default(false).openapi({
    description:
      'If true, this Memory comes from the Timely database/format. Only relevant in Timely FE implementation',
    example: false,
  }),
  raw: z
    .object({})
    .passthrough()
    .openapi({
      example: { info: 'Raw Memory payload (debugging mode only)' },
      description: 'Raw API body, if available (see `getMemories`)',
    })
    .optional(),
  /** Reference to a matching TIC resource, computed at query time via URL pattern matching */
  ref: MemoryRefSchema.optional().openapi({
    description: 'Reference to a matching TIC resource, enabling memory-to-project linking',
  }),
});

export const MemoryWithHourTimestampsSchema = MemorySchema.extend({
  timestamps: z
    .array(
      z.object({
        from: z.string().datetime({ offset: true }),
        to: z.string().datetime({ offset: true }),
        entry_ids: z.array(VariousMemoryIdRefsSchema),
      })
    )
    .optional(),
});

/**
 * Memories via compactMemoriesByProperty lose individual from/to timestamps, which
 * we'll to make `hours.timestamps` adjustments when +'ing "unique" memories
 * This expansion of Memory model is (atm) only used client-side Timely
 * */
export type MemoryWithHourTimestamps = z.infer<typeof MemoryWithHourTimestampsSchema>;

/**
 * When Memories are grouped/merged, this container will attempt to
 * summarize the contents of the group.
 */

export const MemoryGroupSchema = MemorySchema.pick({
  from_timely: true,
  tracker_source: true,
}).extend({
  /** A **virtual** ID for the group, inherited from the **first `memory.id`** that spawned the group */
  id: z
    .number()
    .int()
    .openapi({ example: 1, description: 'Virtual `id`, inherited from first `memory.id`' }),
  v_id: VariousMemoryIdRefsSchema.optional().openapi({
    example: '1',
    description:
      'The scoped id reference to a memory / group `{group_tic_|group_timely}_{id}`. Used as values for `hour.timestamps[].entry_ids:[]`',
  }),
  title: z.string().openapi({ example: 'Inherited from first Memory put in group' }),
  /** The title of the "group_by" strategy used to form the MemoryGroup around this Memory. */
  display_title: z.string().optional().openapi({
    example: 'github',
    description:
      'Title determined by the `group_by` strategy, defaults to `tool`s name. We may want to just use this as `title`',
  }),
  icon_url: z
    .string()
    .url()
    .optional()
    .nullable()
    .describe('Array of (the top) icons in this group'),
  started_at: z.string().datetime({ offset: true }).openapi({
    description: 'Start of this MemoryGroup',
    example: '2021-10-10T10:10:10.000Z',
  }),
  /** End of this Memory (minimum offset from start time may be imposed) */
  ended_at: z.string().datetime({ offset: true }).openapi({
    description: 'End of this MemoryGroup',
    example: '2021-10-10T10:23:33.000Z',
  }),
  color: z.string().default('rgba(86,210,255,0.30)').describe('Color of this group'),
  /** Total seconds of all the Memories in this group */
  total_seconds: z.number().int().openapi({
    example: 12314,
    description: 'Seconds from start of first Memory in group, till end of last Memory.',
  }),
  /**
   * How much of these `total_seconds` was spent actively focused on Memories in this group? 0-1, e.g 0.5 = 50%
   */
  attention_score: z.number().int().min(0).max(100).optional().default(0).openapi({
    example: 69,
    description:
      'How much of these `total_seconds` was spent actively focused on Memories in this group? 0-1, e.g 0.5 = 50%',
  }),
  /** All the Memories of this group */
  memories: z.array(MemorySchema).optional().openapi({
    description:
      'The Memories in this group, optionally sub-grouped by `combineGroupedMemoriesOn` and ordered by `orderGroupedMemoriesBy`',
  }),
  tool: MemoryToolSchema,
  // TODO: Probably add this, look at UI needs first though
  // window_tool: EmbeddedToolLightSchema.optional().nullable(),
  context: MemoryContextSchema,
  category: EmbeddedCategoryLightSchema.nullable(),
  ignored: z
    .boolean()
    .default(false)
    .optional()
    .describe('If true, this MemoryGroup has been (largely) ignored'),
  starred: z
    .boolean()
    .default(false)
    .optional()
    .describe('If true, this MemoryGroup has been marked as important (starred)'),
  /**
   * References the strategy used to create this MemoryGroup
   */
  group_by: GroupMemoriesBySchema,
  /** Display this MemoryGroup as compacted (e.g, only timespan bar) */
  compact: z.boolean().optional().default(false).openapi({
    description: 'If true, this Memory should be displayed in a compact format.',
    example: false,
  }),
  /** @idea Need some way to indicate a group should be "ungrouped", but not super sure how yet */
  signatureKey: z.string().describe('Unique key for this group'),
  /**
   * URL of the first memory in the group, useful for external linking.
   * Inherited from the first memory when the group is created.
   */
  url: z.string().url().optional().nullable().openapi({
    description: 'URL from the first memory in this group, for external linking',
    example: 'https://trello.com/c/xZ8FCcGT',
  }),
  /**
   * TIC resource reference from the first memory, enabling auto-selection of project/task.
   * Inherited from the first memory when the group is created.
   */
  ref: MemoryRefSchema.optional().openapi({
    description: 'Reference to TIC resource from first memory, for project/task auto-selection',
  }),
});

export type MemoryGroup = z.infer<typeof MemoryGroupSchema>;

/**
 * Memory formatted to be used by the AI firehose
 */

export const MemoryForAISchema = MemorySchema.pick({
  id: true,
  title: true,
  started_at: true,
  ended_at: true,
  url: true,
  context: true,
  updated_at: true,
  ignored: true,
  tracker_source: true,
}).extend({
  category_id: z.string().optional().openapi({ example: 'development_tools' }),
  tool_ref: z.string().optional().describe('tool.id or tool_raw').openapi({ example: 'github' }),
  window_tool_ref: z.string().optional().openapi({ example: 'safari' }),
  /** @deprecated, use `tracker_source` instead */
  from_tracker: z
    .boolean()
    .optional()
    .describe(
      'If true, this Memory was created by the Tracker. Deprecated, user `tracker_source` instead.'
    )
    .openapi({
      example: true,
    }),
});

export type MemoryForAI = z.infer<typeof MemoryForAISchema>;

export const MemoryGroupForAISchema = MemoryGroupSchema.pick({
  id: true,
  title: true,
  started_at: true,
  ended_at: true,
}).extend({
  memories: z.array(MemoryForAISchema).optional(),
  category_id: z.string().optional().openapi({ example: 'development_tools' }),
  tool_ref: z.string().optional().describe('tool.id or tool_raw').openapi({ example: 'github' }),
  updated_at: z
    .string()
    .datetime({ offset: true })
    .optional()
    .describe('The most recently updated Memory in this group'),
});

export type MemoryGroupForAI = z.infer<typeof MemoryGroupForAISchema>;

// Use as BaseMemory
export type CreateProviderMemory = z.infer<typeof CreateProviderMemorySchema>;

export const CreateProviderMemorySchema = z.object({
  /**
   * Name of this Memory, typically as a sentence/"something you did"
   */
  title: z
    .string()
    .min(0, { message: 'Title is required' })
    .openapi({ example: 'Pushed code to Timely/app' }),
  description: z.string().openapi({ example: '[some commit message goes here]' }).optional(),
  url: z.string().url().openapi({ example: 'https://github.com/Timely/app' }).optional(),
  verb: VerbsEnum.optional()
    .nullable()
    .openapi({ description: 'Verb of this Memory', example: 'created' }),
  started_at: z
    .string()
    .datetime({ offset: true })
    .openapi({ example: '2021-10-10T10:10:10.000Z' }),
  ended_at: z
    .string()
    .datetime({ offset: true })
    .optional()
    .openapi({ example: '2021-10-10T10:23:33.000Z' }),
  tool_raw: z.string().optional().openapi({ example: 'Github Desktop' }),
  tool_id: z.string().optional().openapi({ example: 'github' }),
  context: MemoryContextSchema.optional(),
  window_tool_raw: z.string().optional().openapi({ example: 'VMWare' }),
  identity: CreateIdentitySchema,
  raw: z
    .object({})
    .optional()
    .openapi({
      example: {
        some: 'json',
        data: 'here',
        or: 'null',
      },
    }),
  conflict_key: z.string().optional().openapi({
    example: '123-456',
    description:
      'Unique key that when provided, would update the existing record (TODO: implement proper extending, it probably overwrites today',
  }),
  /**
   * Reference URIs linking this memory to task/project resources for automatic association.
   * Allows providers to specify which task and/or project this memory relates to via URIs.
   * Note: Inline definition mirrors MemoryRefsSchema from models.memory-tracker.ts to avoid circular imports.
   */
  refs: z
    .object({
      task_uri: z.string().optional().openapi({
        description:
          'URI of the task-level resource this memory relates to (e.g., PR, issue, card)',
        example: 'tic://synthetic/linear/task/abc123',
      }),
      project_uri: z.string().optional().openapi({
        description: 'URI of the project-level resource this memory relates to (e.g., repo, board)',
        example: 'tic://synthetic/linear/project/xyz789',
      }),
    })
    .strict()
    .optional()
    .openapi({
      description:
        'Reference URIs linking this memory to task/project resources for automatic association',
    }),
});

export type MemoryLane = z.infer<typeof MemoryLaneSchema>;

export const MemoryLaneSchema = z.object({
  id: z
    .string()
    .optional()
    .nullable()
    .openapi({ example: 'github', description: 'Can reference a tool.id or category.id' }),
  type: LaneConstructorSchema,
  title: z.string().openapi({ example: 'Github' }),
  sticky: z
    .boolean()
    .optional()
    .default(false)
    .openapi({ example: false, description: 'Should this lane be sticky in the UI?' }),
  compact: z.boolean().optional().default(false).openapi({
    example: false,
    description: 'Should Memories in this lane be compacted in the UI?',
  }),
  ignored: z.boolean().optional().default(false).openapi({
    example: false,
    description:
      'Lanes flagged as ignored still return, but should be hidden in UI based on `showIgnored` setting',
  }),
  dedicated_lane: z.boolean().optional().default(false).openapi({
    example: false,
    description: 'Was created specifically as dedicated lane',
  }),
  icon_url: z
    .string()
    .openapi({ example: 'https://integrations.timelyapp.com/images/app_logo/github.png' })
    .optional(),
  total_seconds: z
    .number()
    .int()
    .openapi({ example: 12314, description: 'Total seconds of the Memories within the lane' }),
  sub_lanes: z.array(z.array(MemoryGroupSchema)),
});

export const CreateMemoryAPISchema = CreateProviderMemorySchema.pick({
  title: true,
  url: true,
  started_at: true,
  ended_at: true,
  context: true,
}).extend({
  app: z.string().optional().openapi({
    example: 'Github Desktop',
    description:
      'Plan text references to an application (technically `tool`) that will be associated during storage RPC',
  }),
});

export type CreateMemoryAPI = z.infer<typeof CreateMemoryAPISchema>;
